import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import moment, { Moment } from "moment-timezone";
import { useAppDispatch, useAppSelector } from "src/hooks/useStoreHooks";
import styles from "./style.module.scss";
import { Avatar, Text, Icons, Input, Button, Spinner } from "@evvve/ui-kit";
import { changeTimeFormat } from "src/helpers/date";
import { ChatSocketIoContext } from "src/shared-features/Wrappers";
import eventGroupChatIcon from "src/assets/icons/event_group_chat.svg";
import {
  Dialog as DialogT,
  DialogType,
  Message,
  MessageType,
  ChatSocketReceiveMessageType,
  CurrentChatPage,
  SystemMessageType,
  ChatSocketReceiveUserMessageType,
  ChatSocketReceiveSystemMessageType,
} from "../types";
import ContextMenu, { ContextMenuTypes } from "src/shared-features/ContextMenu";
import QuitChatModal from "../features/QuitChatModal";
import { UserShort } from "src/store/events/types";
import { ReactComponent as RestrictedToMessageGray400 } from "./icons/restricted_to_message_gray_400.svg";
import { GetUserResponse, UserPrivacy } from "src/store/User/types";
import {
  useGetMessagesMutation,
  useInitMessagesHistoryMutation,
  useGetMembersQuery,
  useDeleteDialogMutation,
  useInitDialogMutation,
  ApiMessageType,
} from "src/store/chat/api";
import { useGetUserQuery, useLazyFollowUserQuery, useLazyUnfollowUserQuery } from "src/store/User/api";
import avatar_skeleton from "src/assets/images/avatar_skeleton.png";
import ViewProfile from "../ViewProfile";
import Participants from "../Participants";
import { addToast } from "src/store/toast/slice";
import { DEFAULT_NOTIFICATION_ESTIMATE, InputMaxLength } from "src/constants";
import { InvitationToGoLive, TheEventIsLive, TheEventStartsIn15Min, TheEventStartsIn3Hours, WelcomeToEvvve } from "../features/SystemNotifications";
import { createMessageFromApi } from "./helpers";
import { useModal } from "src/hooks";
import { useUpdatedDialog } from "../hooks/useUpdatedDialog";
import CurrentDialogId from "../features/wrappers/CurrentDialogId";
import IAmSpeakerAtTheEvent from "../features/SystemNotifications/IAmSpeakerAtTheEvent";
import IAmOrganizingTheEvent from "../features/SystemNotifications/IAmOrganizingTheEvent";
import IWillNotBeASpeakerAtTheEvent from "../features/SystemNotifications/IWillNotBeASpeakerAtTheEvent";
import IAcceptedToBeASpeakerAtTheEvent from "../features/SystemNotifications/IAcceptedToBeASpeakerAtTheEvent";
import WelcomeToTheEvent from "../features/SystemNotifications/WelcomeToTheEvent";
import TheEventHasBeenCancelled from "../features/SystemNotifications/TheEventHasBeenCancelled";
import NewSpeakerAtTheEvent from "../features/SystemNotifications/NewSpeakerAtTheEvent";
import EventDetailsHaveChanged from "../features/SystemNotifications/EventDetailsHaveChanged";
import YouHaveBeenInvitedToBeASpeaker from "../features/SystemNotifications/YouHaveBeenInvitedToBeASpeaker";
import EventDurationExceeded from "../features/SystemNotifications/EventDurationExceeded";
import { decrementFollowingCount, incrementFollowingCount } from "src/store/User/slice";
import { refetchUser } from "src/store/otherUser/otherUserSlice";

const {
  Send,
  ChevronLeft,
  MoreHorizontal,
} = Icons;

const {
  line,
  wrap,
  header,
  dialogs,
  avtar_arrow,
  left_side,
  avatar,
  cloud,
  cloud_wrap,
  cloud_wrap_violet,
  cloud_wrap_gray,
  cloud_violet,
  cloud_gray,
  cloud_avatar_gray,
  triangle,
  triangle_violet,
  triangle_gray,
  cloud_bottom,
  cloud_avatar,
  name_message,
  name_message_time,
  cloud_wrap__is_person,
  cloud_wrap__is_person_violet,
  cloud_wrap__is_person_gray,
  header_title,
  check_and_text,
  check_and_text__is_group,
  send,
} = styles;

interface ChatDialogsProps {
  //dialog? prop means that if dialog is not provided for personal dialog
  //then it'll try to init it with dialogmate id
  dialog?: DialogT,
  dialogType: DialogType,
  dialogMate?: UserShort,
  onBack: () => void,
}

const deleteDialogButtonStatics = {
  [DialogType.Personal]: "Delete",
  [DialogType.Group]: "Leave",
};

const Dialog = (props: ChatDialogsProps) => {
  const dispatch = useAppDispatch();

  const chatDialogRef = useRef<HTMLDivElement | null>(null);
  const isChatDialogScrollFree = useRef(false);
  const messageIdToScroll = useRef<string | null>(null);
  const scrollTop = useRef<number | null>(null);

  const chatMenuEllipsis = useRef<HTMLDivElement>(null);

  const chatSocket = useContext(ChatSocketIoContext);

  const prevDialogId = useContext(CurrentDialogId);
  const returnPrevDialogCallbackRef = useRef<(() => void) | null>(null);

  const hasUnreadMessages = useMemo(() => !!props.dialog?.unreadMessagesCount, []);

  const userToRefetch = useAppSelector(state => state.otherUser.userToRefetch);

  //states
  const userId = useAppSelector(state => state.user.userInfo?.unique_id);

  const [currentPage, setCurrentPage] = useState<CurrentChatPage>(CurrentChatPage.Dialog);

  const [valueInput, setValueInput] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);

  const [dialog, setDialog] = useState<DialogT | null>(props.dialog || null);

  const [dialogMateId, setDialogMateId] = useState<string | null>(props.dialogMate?.unique_id || null);
  const [dialogMate, setDialogMate] = useState<GetUserResponse | null>(null);
  const [messageUserViewProfileId, setMessageUserViewProfileId] = useState<string | null>(null);

  const [isDialogInitialized, setIsDialogInitialized] = useState(!!props.dialog);

  const [messages, setMessages] = useState<Message[] | null>(null);
  const [isMessagesInitialized, setIsMessagesInitialized] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(true);

  const [nextMessagesToken, setNextMessagesToken] = useState<unknown | null>(null);
  const [isNextMessagesExhausted, setIsNextMessagesExhausted] = useState<boolean>(
    props.dialog?.unreadMessagesCount === 0 || false
  );

  const [formerMessagesToken, setFormerMessagesToken] = useState<unknown | null>(null);
  const [isFormerMessagesExhausted, setIsFormerMessagesExhausted] = useState<boolean>(false);

  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState<string | null>(null);

  const [participantsCount, setParticipantsCount] = useState<number | null>(null);

  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const {
    isOpenModal: isQuitDialogModalOpen,
    openModal: openQuitDialogModal,
    closeModal: closeQuitDialogModal,
  } = useModal();

  const [contextMenuPosition, setContextMenuPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const [followUserIsLoading, setFollowUserIsLoading] = useState(false);
  const [unfollowUserIsLoading, setUnfollowUserIsLoading] = useState(false);

  //get updated dialog [concern]
  const updatedDialog = useUpdatedDialog();

  //get messages [concern]
  const [getMessages, getMessagesState] = useGetMessagesMutation();

  //get next messages [concern]
  const getNextMessages = useMemo(() => {
    if (isNextMessagesExhausted || !dialog)
      return;

    return () => dialog?.id ?
      getMessages({ chatId: dialog.id, lastEvaluatedKey: nextMessagesToken, reverse: true }) :
      Promise.reject();
  }, [isNextMessagesExhausted, nextMessagesToken, dialog]);

  //get former messages [concern]
  const getFormerMessages = useMemo(() => {
    if (isFormerMessagesExhausted || !dialog?.id)
      return;

    return () => dialog?.id && getMessages({ chatId: dialog.id, lastEvaluatedKey: formerMessagesToken });
  }, [isFormerMessagesExhausted, formerMessagesToken, dialog]);

  //get latest messages [concern]
  const refreshMessages: (() => Promise<unknown>) | undefined = useMemo(() => {
    if (!isDialogInitialized || !dialog?.id)
      return;

    return () => {
      setMessages(null);
      setNextMessagesToken(null);
      setIsNextMessagesExhausted(true);
      setFormerMessagesToken(null);
      setIsFormerMessagesExhausted(false);
      setFirstUnreadMessageId(null);
      return dialog?.id ? getMessages({ chatId: dialog.id }) : Promise.reject();
    };
  }, [isDialogInitialized, dialog]);

  //init messages history [concern]
  const [initMessagesHistory, initMessagesHistoryState] = useInitMessagesHistoryMutation();

  //begin messages [concern]
  useEffect(() => {
    if (isDialogInitialized && dialog?.id)
      if (hasUnreadMessages)
        initMessagesHistory({ chatId: dialog.id });
      else
        getMessages({ chatId: dialog.id });

  }, [isDialogInitialized, dialog?.id]);

  //start receiving messages [concern][reflex]
  useEffect(() => {
    if (!isDialogInitialized || !isNextMessagesExhausted || chatSocket?.connecting || !chatSocket)
      return;

    const onReceiveMessageHandler = (msg: ChatSocketReceiveMessageType) => {
      if (msg.message.chat_id !== dialog?.id)
        return;

      let m: ApiMessageType | null = null;

      if ((msg as ChatSocketReceiveUserMessageType).user) {
        const user = {
          ...(msg as ChatSocketReceiveUserMessageType).user,
          id: (msg as ChatSocketReceiveUserMessageType).user.user_id,
        };
        m = {
          ...(msg as ChatSocketReceiveUserMessageType).message,
          type: MessageType.User,
          user,
        };
      } else if ((msg as ChatSocketReceiveSystemMessageType).message.type === MessageType.System)
        m = (msg as ChatSocketReceiveSystemMessageType).message;

      const message = m && createMessageFromApi(m);

      message && setMessages(prev => {
        if (!prev)
          return [message];

        return [...prev, message];
      });
    };

    chatSocket?.chatSocket?.on("receiveMessage", onReceiveMessageHandler);
    return () => {
      chatSocket?.chatSocket?.off("receiveMessage", onReceiveMessageHandler);
    };
  }, [
    chatSocket,
    isDialogInitialized,
    isNextMessagesExhausted]);

  //get members [concern]
  const getMembersState = useGetMembersQuery({ chatId: dialog?.id || "" }, { skip: !dialog?.id });

  useEffect(() => {
    currentPage === CurrentChatPage.Dialog &&
    !getMembersState.isUninitialized && getMembersState.refetch();
  }, [currentPage === CurrentChatPage.Dialog]);

  //get user [concern]
  const getUserState = useGetUserQuery(
    { userId: dialogMateId || "" },
    { skip: !dialogMateId }
  );

  //quit dialog [concern]
  //quit dialog is loading [reflex][state]
  const [deleteDialog, deleteDialogState] = useDeleteDialogMutation();
  const quitDialog = useCallback(() => {
    isDialogInitialized && dialog?.id && deleteDialog({ chatId: dialog.id }).
      then(() => props.onBack());
  }, [dialog]);

  //create personal dialog [concern]
  const [initDialog, initDialogState] = useInitDialogMutation();
  const createDialog = useMemo(() => {
    const dm = props.dialogMate;
    if (!dm)
      return;

    return (msg: string) => initDialog({ userId: dm.unique_id, message: msg });
  }, []);

  //send message [concern]s
  useEffect(() => {
    if (chatSocket && chatSocket.chatSocket && dialog && isDialogInitialized) {
      chatSocket.chatSocket.emit("chooseChat", { chat_id: dialog.id });
    }
  }, [chatSocket, dialog?.id, isDialogInitialized]);

  //if dialog leave then socket io emit choose chat prev dialog [concern]
  useEffect(() => {
    if (!chatSocket?.chatSocket)
      return;

    if (prevDialogId)
      returnPrevDialogCallbackRef.current = () => chatSocket.chatSocket?.emit("chooseChat", { chat_id: prevDialogId });
  }, [chatSocket?.chatSocket]);

  useEffect(() => (returnPrevDialogCallbackRef.current || void 0), []);

  const sendMessageHandler = useCallback(
    (msg: string) => {
      if (!msg)
        return;

      if (isDialogInitialized) {
        if (!chatSocket || !chatSocket.chatSocket)
          return;

        isChatDialogScrollFree.current = false;

        if (!isNextMessagesExhausted && refreshMessages) {
          refreshMessages().
            then(() => {
              // props.startReceivingMessages?.();
              if (!chatSocket || !chatSocket.chatSocket)
                return;

              chatSocket.chatSocket.emit("sendMessage", { message: msg });
            });
          return;
        }

        chatSocket.chatSocket.emit("sendMessage", { message: msg });
      } else {
        createDialog?.(msg);
      }
    },
    [chatSocket, isDialogInitialized, isNextMessagesExhausted, createDialog]
  );

  useEffect(() => {
    if (isInputFocused) {
      const sendMessageOnEnterHandler = (event: KeyboardEvent) => {
        if (event.key === "Enter") {
          const vits = valueInput.trim().slice(0, InputMaxLength.XXXL_4096);
          sendMessageHandler(vits);
          setValueInput("");
        }
      };
      window.addEventListener("keypress", sendMessageOnEnterHandler);
      return () => removeEventListener("keypress", sendMessageOnEnterHandler);
    }
  }, [isInputFocused, valueInput]);


  //update context menu position [concern][reflex]
  const updateMenuPosition = useCallback(() => {
    if (chatMenuEllipsis.current) {
      const rectBoundary = chatMenuEllipsis.current.getBoundingClientRect();
      const left = rectBoundary.right;
      const top = rectBoundary.top;
      setContextMenuPosition({ left, top });
    }
  }, []);

  //update context menu position on resize or scroll [concern][reflex]
  useEffect(() => {
    if(!isContextMenuOpen)
      return;

    const closeContextMenu = () => setIsContextMenuOpen(false);

    document.addEventListener("scroll", closeContextMenu);
    window.addEventListener("resize", closeContextMenu);
    return () => {
      document.removeEventListener("scroll", closeContextMenu);
      window.removeEventListener("resize", closeContextMenu);
    };

  }, [isContextMenuOpen]);

  //messages scroll handler [concern]
  const chatDialogScrollHandler = useCallback(() => {
    const element = chatDialogRef.current;
    if (!element)
      return;

    scrollTop.current = element.scrollTop;

    const isScrollBottomed = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) <= 1;

    if (isScrollBottomed) {
      isChatDialogScrollFree.current = false;
      if (!isNextMessagesExhausted && getNextMessages) {
        messageIdToScroll.current = messages?.at(-1)?.id ?? null;
        getNextMessages();
      }
    } else
      isChatDialogScrollFree.current = true;

    if (element.scrollTop === 0 && !isFormerMessagesExhausted && getFormerMessages) {
      messageIdToScroll.current = messages?.[0]?.id ?? null;
      getFormerMessages();
    }

  }, [
    isNextMessagesExhausted,
    getNextMessages,
    isFormerMessagesExhausted,
    getFormerMessages,
  ]);

  //update scroll if messages updated [concern]
  useEffect(() => {
    if(chatDialogRef.current && !isChatDialogScrollFree.current) {
      chatDialogRef.current.scrollTop = chatDialogRef.current.scrollHeight;
    }
  }, [messages]);

  //update scroll if return to dialog [concern]
  useEffect(() => {
    if (currentPage !== CurrentChatPage.Dialog)
      return;

    if (!scrollTop.current || !chatDialogRef.current)
      return;

    if (messageIdToScroll.current)
      return;

    chatDialogRef.current.scrollTop = scrollTop.current;

  }, [currentPage === CurrentChatPage.Dialog]);

  //update last read message [concern]
  useEffect(() => {
    if (!chatSocket || !chatSocket.chatSocket)
      return;

    const lastMessageId = messages?.at(-1)?.id;

    lastMessageId && chatSocket.chatSocket.emit("lastReadMessage", { message_id: lastMessageId });
  }, [messages]);

  //is user to refetch
  useEffect(() => {
    if (!userToRefetch || getUserState.isUninitialized)
      return;

    getUserState.refetch();

  }, [userToRefetch]);

  //follow user [state] [concern]s [reflex]
  const [followUserQuery, followUserQueryState] = useLazyFollowUserQuery();

  useEffect(() => {
    if (followUserQueryState.isUninitialized ||
      followUserQueryState.isLoading)
      return;

    if (followUserQueryState.isError) {
      dispatch(addToast({
        type: "default",
        content: "Something went wrong. Please try later",
        estimate: DEFAULT_NOTIFICATION_ESTIMATE,
      }));
      return;
    }

    dispatch(incrementFollowingCount());

    dialogMate?.user.unique_id &&
    dialogMate?.user.username &&
    dispatch(refetchUser({ userId: dialogMate.user.unique_id, username: dialogMate.user.username }));

  }, [followUserQueryState]);

  const followDialogmate = useMemo(() => {
    if (!dialogMate)
      return null;

    return () => followUserQuery({ uniqueId: dialogMate.user.unique_id });

  }, [dialogMate]);

  //unfollow user [state] [concern]s [reflex]
  const [unfollowUserQuery, unfollowUserQueryState] = useLazyUnfollowUserQuery();
  useEffect(() => {
    if (unfollowUserQueryState.isUninitialized ||
      unfollowUserQueryState.isLoading)
      return;

    if (unfollowUserQueryState.isError) {
      dispatch(addToast({
        type: "default",
        content: "Something went wrong. Please try later",
        estimate: DEFAULT_NOTIFICATION_ESTIMATE,
      }));
      return;
    }

    dispatch(decrementFollowingCount());

    dialogMate?.user.unique_id &&
    dialogMate?.user.username &&
    dispatch(refetchUser({ userId: dialogMate.user.unique_id, username: dialogMate.user.username }));

  }, [unfollowUserQueryState]);

  const unfollowDialogmate = useMemo(() => {
    if (!dialogMate)
      return null;

    return () => unfollowUserQuery({ uniqueId: dialogMate.user.unique_id });

  }, [dialogMate]);

  //dialog [reflex]
  useEffect(() => {
    updatedDialog && setDialog(pd => (pd && {
      ...pd,
      title: updatedDialog.title,
      avatarUrl: updatedDialog.avatarUrl,
    }));
  }, [updatedDialog]);

  //participants count [reflex]
  useEffect(() => {
    updatedDialog && setParticipantsCount(updatedDialog.participantsCount);
  }, [updatedDialog]);

  //dialogmate id [reflex]
  useEffect(() => {
    if (props.dialogType !== DialogType.Personal)
      return;

    const members = getMembersState.data;
    if (!members)
      return;

    const dm = members.members.find((m) => m.id !== userId);
    dm && setDialogMateId(dm.id);

  }, [props.dialogType, getMembersState]);

  //dialogmate [reflex]
  useEffect(() => {
    const u = getUserState.data;
    if (!u)
      return;

    setDialogMate(u);
  }, [getUserState]);

  //init personal dialog [reflex]
  //draft
  useEffect(() => {
    if (props.dialogType !== DialogType.Personal ||
      !props.dialogMate ||
      isDialogInitialized ||
      dialog)
      return;

    const personalDialog = {
      type: DialogType.Personal,
      system: false,
      title: props.dialogMate.first_name + " " + props.dialogMate.last_name,
      avatarUrl: props.dialogMate.avatar_url || avatar_skeleton,
      isVerified: true,
      isRead: true,
      isOnline: true,
      isWarning: false,
      isMute: false,
      messages: null,
    };

    setDialog(personalDialog);

  }, []);

  //real
  useEffect(() => {
    if (initDialogState.isError) {
      dispatch(addToast({
        type: "default",
        content: "Something went wrong. Please try later",
        estimate: DEFAULT_NOTIFICATION_ESTIMATE,
      }));
      return;
    }

    if (!initDialogState.data)
      return;

    const ids = initDialogState.data;

    const d = {
      id: ids.chat_id,
      system: false,
      type: ids.type,
      title: ids.title,
      avatarUrl: ids.avatar_url,
      isVerified: true,
      isRead: true,
      isOnline: true,
      isWarning: false,
      isMute: false,
      messages: null,
    };

    setDialog(d);

  }, [initDialogState]);

  //is dialog initialized [reflex]
  useEffect(() => {
    if (initDialogState.isError ||
      initDialogState.isLoading ||
      initDialogState.isUninitialized)
      return;

    setIsDialogInitialized(true);

  }, [initDialogState]);

  //messages [reflex]
  useEffect(() => {
    const messagesApi = getMessagesState.data?.messages;
    if (messagesApi?.length === 0 || !messagesApi)
      return;

    const messages: (Message | null)[] = messagesApi.map(createMessageFromApi);
    const filteredMessages = messages.filter((m): m is Message => m !== null);

    if (getMessagesState.originalArgs?.reverse) {
      setMessages(prev => (prev?.length ? [...prev, ...filteredMessages] : filteredMessages));
      return;
    }

    filteredMessages.reverse();
    setMessages(prev => (prev?.length ? [...filteredMessages, ...prev] : filteredMessages));

  }, [getMessagesState.data]);

  useEffect(() => {
    !getMessagesState.isUninitialized && setIsMessagesInitialized(true);
  }, [getMessagesState.isUninitialized]);

  useEffect(() => {
    !getMessagesState.isUninitialized && !getMessagesState.isLoading && setIsMessagesLoading(false);
  }, [getMessagesState.isLoading]);

  useEffect(() => {
    const { data } = initMessagesHistoryState;
    if (data) {
      const { oldest, last_read_message: lastReadMessage, newest } = data;

      const messagesApi: ApiMessageType[] = [];
      oldest && messagesApi.push(...oldest.messages);
      lastReadMessage && messagesApi.push(lastReadMessage);
      newest && messagesApi.push(...newest.messages);

      if (messagesApi.length === 0) {
        return;
      }

      const messages: (Message | null)[] = messagesApi.
        map(createMessageFromApi);

      const filteredMessages = messages.filter((m): m is Message => (m !== null));

      setMessages(filteredMessages);
    }
  }, [initMessagesHistoryState.data]);

  useEffect(() => {
    !initMessagesHistoryState.isUninitialized && setIsMessagesInitialized(true);
  }, [initMessagesHistoryState.isUninitialized]);

  useEffect(() => {
    !initMessagesHistoryState.isUninitialized && !initMessagesHistoryState.isLoading && setIsMessagesLoading(false);
  }, [initMessagesHistoryState.isLoading]);

  //next messages token [reflex]s
  useEffect(() => {
    if (getMessagesState.isUninitialized)
      return;

    const token = getMessagesState.data?.last_evaluated_key;
    const isReverse = getMessagesState.originalArgs?.reverse;
    if (isReverse)
      setNextMessagesToken(token);

  }, [getMessagesState.data]);

  useEffect(() => {
    if (initMessagesHistoryState.isUninitialized)
      return;

    const token = initMessagesHistoryState.data?.newest?.last_evaluated_key;
    token && setNextMessagesToken(token);
    !token && setIsNextMessagesExhausted(true);

  }, [initMessagesHistoryState.data]);

  //former messages token [reflex]s
  useEffect(() => {
    if (getMessagesState.isUninitialized)
      return;

    const token = getMessagesState.data?.last_evaluated_key;
    const isReverse = getMessagesState.originalArgs?.reverse;
    if (!isReverse)
      setFormerMessagesToken(token);

  }, [getMessagesState.data]);

  useEffect(() => {
    if (initMessagesHistoryState.isUninitialized)
      return;

    const token = initMessagesHistoryState.data?.oldest?.last_evaluated_key;
    token && setFormerMessagesToken(token);
    !token && setIsFormerMessagesExhausted(true);

  }, [initMessagesHistoryState.data]);

  //is next messages exhausted [reflex]
  useEffect(() => {
    if (getMessagesState.isUninitialized)
      return;

    const token = getMessagesState.data?.last_evaluated_key;
    const isReverse = getMessagesState.originalArgs?.reverse;

    if (isReverse)
      token === null && setIsNextMessagesExhausted(true);
  }, [getMessagesState.data]);

  //is former messages exhausted [reflex]
  useEffect(() => {
    if (getMessagesState.isUninitialized)
      return;

    const token = getMessagesState.data?.last_evaluated_key;
    const isReverse = getMessagesState.originalArgs?.reverse;

    if (!isReverse)
      token === null && setIsFormerMessagesExhausted(true);
  }, [getMessagesState.data]);

  //first unread message id [reflex]
  useEffect(() => {
    const { data } = initMessagesHistoryState;

    if (!data)
      return;

    const { newest } = data;
    const fum = newest?.messages[0];
    if (fum) {
      setFirstUnreadMessageId(fum.id);
      messageIdToScroll.current = fum.id;
    }

  }, [initMessagesHistoryState.data]);

  //participants count [reflex]
  useEffect(() => {
    if (!getMembersState.currentData || getMembersState.isFetching)
      return;

    const psc = getMembersState.currentData.members.length;
    setParticipantsCount(psc);

  }, [getMembersState.currentData]);

  //context menu position [reflex]
  useEffect(() => {
    if (!isContextMenuOpen)
      return;

    updateMenuPosition();

  }, [isContextMenuOpen]);

  // follow user is loading [reflex]s
  useEffect(() => {
    followUserQueryState.isFetching && setFollowUserIsLoading(true);

  }, [followUserQueryState.isFetching]);

  useEffect(() => {
    getUserState.isError && setFollowUserIsLoading(false);

  }, [getUserState]);

  useEffect(() => {
    setFollowUserIsLoading(false);

  }, [dialogMate]);

  // unfollow user is loading [reflex]s
  useEffect(() => {
    unfollowUserQueryState.isFetching && setUnfollowUserIsLoading(true);

  }, [unfollowUserQueryState.isFetching]);

  useEffect(() => {
    getUserState.isError && setUnfollowUserIsLoading(false);

  }, [getUserState]);

  useEffect(() => {
    setUnfollowUserIsLoading(false);

  }, [dialogMate]);

  //

  const isGroup = useMemo(() => dialog && dialog.type === DialogType.Group, [dialog?.type]);
  const isPersonal = useMemo(() => dialog && dialog.type === DialogType.Personal, [dialog?.type]);
  const printCheckAndTime = (even: boolean, date: string | Date | Moment) => (
    <div className={
      [
        check_and_text,
        isGroup && check_and_text__is_group
      ].join(" ")
    }>
      <Text
        size="s"
        color="grayscale400"
      >
        {changeTimeFormat(date)}
      </Text>
      {/* {even && (
        <div className={cloud_check}>
          <Check color="violet500" />
        </div>
      )} */}
    </div>
  );

  const messagesList = useMemo(() => (
    dialog && userId && [...(messages || [])].map((m, i, ms) => {
      const isThisUserMessage = m.type === MessageType.User && m.userId === userId;

      const nm = i < (ms.length - 1) && ms[i+1];
      const isNextMessageUserChanged = !(m.type === MessageType.User &&
        nm && nm.type === MessageType.User &&
        nm.userId === m.userId);

      const todayDate = moment(Date.now());
      const messageDate = moment.utc(m.datetime).local();
      const isMessageDateToday = todayDate.format("LL") === messageDate.format("LL");

      const prevDate = i > 0 && moment.utc(ms[i-1].datetime).
        local();
      const isMessageDateChanged = !(prevDate &&
        messageDate.format("MMMM DD") === prevDate.format("MMMM DD"));

      let message = null;
      if (m.type === MessageType.User) {
        const isCurrentUserMessage = m.userId === userId;

        const cloudStyle = isCurrentUserMessage
          ? cloud_wrap__is_person_violet
          : cloud_wrap__is_person_gray;

        message = <div
          className={[
            cloud_wrap,
            isPersonal ? cloud_wrap__is_person : "",
            m.userId === userId ? cloud_wrap_violet : cloud_wrap_gray,
          ].join(" ")}
        >
          <div
            className={[
              cloud,
              isThisUserMessage ? cloud_violet : cloud_gray,
              isPersonal ? cloudStyle : "",
            ].join(" ")}
          >
            {m.caption && (
              <div className={name_message}>
                <span
                  style={{ cursor: m.userId && userId !== m.userId ? "pointer" : "unset" }}
                  onClick={() => {
                    if (m.userId && userId !== m.userId) {
                      setMessageUserViewProfileId(m.userId);
                      setCurrentPage(CurrentChatPage.MessageUserViewProfile);
                    }
                  }}>
                  <Text size="s" color="violet600">
                    {m.caption}
                  </Text>
                </span>
                <div className={name_message_time}>
                  {printCheckAndTime(isThisUserMessage, moment.utc(m.datetime).
                    local().
                    format())}
                </div>
              </div>
            )}
            <div className={cloud_bottom}>
              <Text size="s" color="grayscale1000">
                {m.text}
              </Text>
              {!m.caption && printCheckAndTime(isThisUserMessage, moment.utc(m.datetime).
                local().
                format())}
            </div>
            {isNextMessageUserChanged && (
              <div
                className={[
                  triangle,
                  isThisUserMessage ? triangle_violet : triangle_gray,
                ].join(" ")}
              />
            )}
            {isNextMessageUserChanged &&
              !isThisUserMessage && isGroup && (
              <div
                className={[
                  cloud_avatar,
                  cloud_avatar_gray,
                ].join(" ")}
              >
                <Avatar status="offline" size="xs" src={m.avatarUrl || avatar_skeleton} alt="avatar" />
              </div>
            )}
          </div>
        </div>;
      } else if (m.type === MessageType.System)
        if (m.systemMessageType === SystemMessageType.IAmSpeakerAtTheEvent)
          message = <IAmSpeakerAtTheEvent message={m} />;
        else if (m.systemMessageType === SystemMessageType.IWillNotBeASpeakerAtTheEvent)
          message = <IWillNotBeASpeakerAtTheEvent message={m} />;
        else if (m.systemMessageType === SystemMessageType.YouHaveBeenInvitedToBeASpeaker)
          message = <YouHaveBeenInvitedToBeASpeaker message={m} />;
        else if (m.systemMessageType === SystemMessageType.EventDetailsHaveChanged)
          message = <EventDetailsHaveChanged message={m} />;
        else if (m.systemMessageType === SystemMessageType.NewSpeakerAtTheEvent)
          message = <NewSpeakerAtTheEvent message={m} />;
        else if (m.systemMessageType === SystemMessageType.WelcomeToTheEvent)
          message = <WelcomeToTheEvent message={m} />;
        else if (m.systemMessageType === SystemMessageType.TheEventHasBeenCancelled)
          message = <TheEventHasBeenCancelled message={m} />;
        else if (m.systemMessageType === SystemMessageType.IAcceptedToBeASpeakerAtTheEvent)
          message = <IAcceptedToBeASpeakerAtTheEvent message={m} />;
        else if (m.systemMessageType === SystemMessageType.IAmOrganizingTheEvent)
          message = <IAmOrganizingTheEvent message={m} />;
        else if (m.systemMessageType === SystemMessageType.InvitationToGoLive)
          message = <InvitationToGoLive message={m} />;
        else if (m.systemMessageType === SystemMessageType.WelcomeToEvvve)
          message = <WelcomeToEvvve />;
        else if (m.systemMessageType === SystemMessageType.EventDurationExceeded)
          message = <EventDurationExceeded />;
        else if (m.systemMessageType === SystemMessageType.TheEventStartsIn3Hours)
          message = <TheEventStartsIn3Hours message={m} />;
        else if (m.systemMessageType === SystemMessageType.TheEventStartsIn15Min)
          message = <TheEventStartsIn15Min message={m} />;
        else if (m.systemMessageType === SystemMessageType.TheEventIsLive)
          message = <TheEventIsLive message={m} />;

      const messageClassNames = [];
      if (i === 0)
        messageClassNames.push(styles.message);

      if (ms.length - 1 === i)
        messageClassNames.push(styles.lastMessage);

      return (
        <div key={m.id}
          className={messageClassNames.join(" ")}
          ref={(node) => {
            if (m.id === messageIdToScroll.current)
              requestAnimationFrame(() => {
                if (!chatDialogRef.current || !node)
                  return;

                chatDialogRef.current.scrollTop = node.offsetTop - chatDialogRef.current.offsetHeight / 2;
                messageIdToScroll.current = null;
                isChatDialogScrollFree.current = true;
              });
          }}>
          {m.id === firstUnreadMessageId && (
            <div className={line}>
              <Text size="xs" as="p" color="grayscale600">
                Unread&nbsp;messages
              </Text>
            </div>
          )}
          {(i === 0 || isMessageDateChanged) && (
            <div className={line}>
              <Text size="xs" as="p" color="grayscale600">
                {!isMessageDateToday ? messageDate.format("MMMM DD") : "Today"}
              </Text>
            </div>
          )}
          {message}
        </div>);
    })), [dialog, messages, firstUnreadMessageId]);

  const messagesPanel = useMemo(() => {
    if (!isDialogInitialized)
      return (
        <div className={styles.noYetMessagesWrap}>
          <Icons.Smile size="m" color="grayscale600" />
          <Text size="m">
            You haven’t chatted yet.<br />
            Send a message to start<br />
            networking</Text>
        </div>
      );

    if (isMessagesInitialized && !isMessagesLoading)
      return messages ?
        messagesList :
        <div className={styles.noYetMessagesWrap}>
          <Icons.Smile size="m" color="grayscale600" />
          <Text size="m">
            This is where all event<br />
            communication magic will<br />
            happen. Be the first to<br />
            greet future participants!</Text>
        </div>;

    return (
      <div className={styles.spinner}>
        <Spinner size="l" color="grayscale400" />
      </div>);

  }, [isDialogInitialized, isMessagesInitialized, isMessagesLoading, messages]);


  const contextMenuActions = useMemo(() => {
    if (props.dialogType === DialogType.Group)
      return [
        {
          type: ContextMenuTypes.ViewAllParticipants,
          handler: () => setCurrentPage(CurrentChatPage.Participants)
        },
        {
          type: ContextMenuTypes.LeaveGroup,
          handler: () => {
            setIsContextMenuOpen(false);
            openQuitDialogModal();
          }
        }
      ];

    if (props.dialogType === DialogType.Personal){
      const actions = [];

      actions.push({
        type: ContextMenuTypes.ViewProfile,
        handler: () => setCurrentPage(CurrentChatPage.ViewProfile)
      });

      if (dialogMate) {
        dialogMate.privacy === UserPrivacy.public && (!dialogMate.is_following ?
          actions.push({
            type: ContextMenuTypes.FollowUser,
            handler: followDialogmate,
            isLoading: followUserIsLoading,
          }) :
          actions.push({
            type: ContextMenuTypes.UnfollowUser,
            handler: unfollowDialogmate,
            isLoading: unfollowUserIsLoading,
          }));

      } else {
        actions.push({
          type: ContextMenuTypes.FollowUser,
          isLoading: true,
        });
      }

      actions.push({
        type: ContextMenuTypes.DeleteDialog,
        handler: () => {
          setIsContextMenuOpen(false);
          openQuitDialogModal();
        }
      });

      return actions;
    }

    return [];
  }, [props.dialogType, dialogMate, followUserIsLoading, unfollowUserIsLoading]);

  if (!dialog || props.dialogType === DialogType.Personal && dialogMate === null)
    return (
      <div className={styles.spinner}>
        <Spinner size="l" color="grayscale400" />
      </div>);

  const normalMessageInput = <>
    <div className={styles.input_wrap}>
      <Input
        value={valueInput}
        placeholder="Type your message"
        onChange={(e) => {
          const v = e.currentTarget.value;
          const vt = e.currentTarget.value.trimStart();
          const startWhiteSpaceCount = v.length - vt.length;
          const vs = vt.slice(0, 4096);
          const s = " ".repeat(startWhiteSpaceCount) + vs;
          setValueInput(s);
        }}
        ref={(input: HTMLImageElement) => {
          input && input.addEventListener("focus", () => setIsInputFocused(true));
          input && input.addEventListener("blur", () => setIsInputFocused(false));
        }}
      />
    </div>
    {valueInput.trim() && (
      <button
        className={send}
        onClick={() => {
          const vits = valueInput.trim().slice(0, InputMaxLength.XXXL_4096);
          sendMessageHandler(vits);
          setValueInput("");
        }}
      >
        <Send color="violet600" size="m" />
      </button>
    )}
  </>;

  let footer;
  if (props.dialogType === DialogType.Group)
    footer = <footer className={styles.footer}>
      { normalMessageInput }
    </footer>;

  if (props.dialogType === DialogType.Personal) {
    if (dialogMate === null)
      footer = null;
    else if (dialogMate.is_can_start_chat)
      footer = <footer className={styles.footer}>
        {normalMessageInput}
      </footer>;
    else if (!dialogMate.is_can_start_chat && dialogMate.user.position === "Support Specialist")
      footer = null;
    else footer = <footer className={styles.footer}>
      <div className={styles.restrictedToMessageWrap}>
        <div className={styles.icon}>
          <RestrictedToMessageGray400 />
        </div>
        <Text size="m" color="grayscale800">
          {dialogMate.user.first_name} has restricted messages from other users
        </Text>
      </div>
    </footer>;
  }

  const dialogElement = (
    <>
      <div className={wrap}>
        <header className={header}>
          <div className={left_side}>
            <div className={avtar_arrow}>
              <div onClick={props.onBack}>
                <ChevronLeft size="m" color="violet600" />
              </div>
              <div className={avatar} onClick={() => {
                if (dialog.type === DialogType.Group)
                  setCurrentPage(CurrentChatPage.Participants);
                else if (dialog.type === DialogType.Personal && !dialog.system)
                  setCurrentPage(CurrentChatPage.ViewProfile);
              }}>
                <Avatar
                  src={dialog.avatarUrl || eventGroupChatIcon}
                  size="m"
                  status={dialog.isOnline && isPersonal ? "online" : "offline"}
                  alt={`Icon: ${dialog.title}`}
                />
              </div>
            </div>
            <div className={
              {
                [DialogType.Group]: styles.header_info_group,
                [DialogType.Personal]: styles.header_info_personal,
              }[dialog.type]
            }
            onClick={() => {
              if (dialog.type === DialogType.Group)
                setCurrentPage(CurrentChatPage.Participants);
              else if (dialog.type === DialogType.Personal && !dialog.system)
                setCurrentPage(CurrentChatPage.ViewProfile);
            }}>
              <div className={header_title}>
                <Text size="m" color="grayscale1000">
                  {dialog.title}
                </Text>
              </div>
              {isGroup && participantsCount && (
                <Text size="xs" color="grayscale600">
                  {`${participantsCount} ${participantsCount > 1 ? "participants" : "participant"}`}
                </Text>
              )}
            </div>
          </div>
          {dialogMate?.user.position !== "Support Specialist" &&
            <div ref={chatMenuEllipsis} onClick={() => setIsContextMenuOpen(true)}>
              <MoreHorizontal size="m" color="violet600" />
            </div>}
        </header>
        <div
          className={dialogs}
          onScroll={chatDialogScrollHandler}
          ref={chatDialogRef}
        >
          {messagesPanel}
        </div>
        { footer }
      </div>
      {isContextMenuOpen && <ContextMenu
        onClickBlockingArea={() => setIsContextMenuOpen(false)}
        position={{
          left: contextMenuPosition?.left,
          top: contextMenuPosition?.top,
        }}
        actions={contextMenuActions}
      />}
      {isQuitDialogModalOpen &&
      dialog && <QuitChatModal
        dialogType={props.dialogType}
        dialogAvatarUrl={dialog.avatarUrl}
        dialogCaption={dialog.title}
        onClose={closeQuitDialogModal}
        buttons={
          <div className={styles.leaveGroupModalButtons}>
            <Button
              type="outline"
              theme="violet"
              onClick={closeQuitDialogModal}
            >
              Cancel
            </Button>
            <Button
              type="solid"
              theme="violet"
              onClick={quitDialog}
            >
              {deleteDialogState.isLoading ? <Spinner size="m" /> : deleteDialogButtonStatics[dialog.type]}
            </Button>
          </div>
        }
      />}
    </>
  );

  if (currentPage === CurrentChatPage.Dialog)
    return dialogElement;

  if (currentPage === CurrentChatPage.MessageUserViewProfile)
    if (messageUserViewProfileId)
      return (
        <CurrentDialogId.Provider value={dialog.id}>
          <ViewProfile
            memberId={messageUserViewProfileId}
            onClose={() => {
              setCurrentPage(CurrentChatPage.Dialog);
              setMessageUserViewProfileId(null);
            }}
          />
        </CurrentDialogId.Provider>
      );
    else
      return null;

  if (currentPage === CurrentChatPage.ViewProfile)
    if (dialogMateId)
      return (
        <CurrentDialogId.Provider value={dialog.id}>
          <ViewProfile
            memberId={dialogMateId}
            onClose={() => setCurrentPage(CurrentChatPage.Dialog)}
          />
        </CurrentDialogId.Provider>
      );
    else
      return null;

  if (currentPage === CurrentChatPage.Participants && dialog?.id)
    return (
      <CurrentDialogId.Provider value={dialog.id}>
        <Participants
          dialogId={dialog.id}
          onBack={() => setCurrentPage(CurrentChatPage.Dialog)}
        />
      </CurrentDialogId.Provider>
    );

  return null;
};
export default Dialog;